import { useEffect, useState } from 'react'
import { Globe, ChevronDown } from 'lucide-react'
import { useLanguageSwitcher } from '../contexts/LocalizationContext'
import { cn } from './ui'

export default function LanguageSwitcher() {
  const { currentLanguage, supportedLanguages, switchLanguage } = useLanguageSwitcher()
  const [isOpen, setIsOpen] = useState(false)

  const currentLanguageConfig = supportedLanguages[currentLanguage]

  const handleLanguageChange = (languageCode: string) => {
    switchLanguage(languageCode as any)
    setIsOpen(false)
  }

  // Esc closes the dropdown (a11y).
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen])

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="h-10 inline-flex items-center gap-2 px-3 rounded-xl text-ink-muted hover:text-ink hover:bg-surface-elevated transition-colors"
      >
        <Globe size={18} />
        <span className="text-sm font-medium">
          {currentLanguageConfig?.flag} {currentLanguageConfig?.nativeName}
        </span>
        <ChevronDown size={14} className={cn('transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <>
          <div role="listbox" aria-label="Language" className="absolute right-0 mt-2 w-52 rounded-xl bg-surface-elevated border border-line shadow-lg py-1 z-50 animate-scale-in origin-top-right">
            {Object.entries(supportedLanguages).map(([code, config]) => {
              const active = code === currentLanguage
              return (
                <button
                  key={code}
                  onClick={() => handleLanguageChange(code)}
                  role="option"
                  aria-selected={active}
                  className={cn(
                    'flex items-center w-full px-3 py-2 text-sm transition-colors',
                    active
                      ? 'bg-brand/10 text-brand'
                      : 'text-ink hover:bg-surface',
                  )}
                >
                  <span className="mr-3">{config.flag}</span>
                  <span className="font-medium">{config.nativeName}</span>
                  {active && <span className="ml-auto text-accent">✓</span>}
                </button>
              )
            })}
          </div>

          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
        </>
      )}
    </div>
  )
}
